import React, { useState } from 'react';
import { Controller, useFieldArray, Control, UseFormSetValue, UseFormWatch, UseFormTrigger } from 'react-hook-form';
import { ArrowPathIcon, PlusCircleIcon, StarIcon, TrashIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';
import { useResourceOptions, ResourceType, Technology, Seniority } from '@/hooks/useResourceOptions';
import { Resource } from '@/features/Collaborators/Models/collaborator';
import { ResourceTypeList, TechnologyList } from '@/components/shared/ResourceCapabilitySelector';
import { useToast } from '@/hooks/useToast';
import { CapabilitySearchInput } from '@/components/shared';
import { useCapabilitySearch } from '@/hooks/useCapabilitySearch';

interface CapabilitiesFieldsetProps {
    context: CollaboratorFormContext;
    control: Control<any>; // Control do React Hook Form do formulário pai
    setValue: UseFormSetValue<any>; // SetValue do React Hook Form do formulário pai
    watch: UseFormWatch<any>; // Watch do React Hook Form do formulário pai
    trigger?: UseFormTrigger<any>; // Trigger do React Hook Form do formulário pai
    errors?: Record<string, any>; // Erros do formulário pai
    isSubmitting?: boolean; // Estado de submissão via props
    layout?: 'full' | 'compact'; // Layout via props
}

const CapabilitiesFieldset: React.FC<CapabilitiesFieldsetProps> = ({
    context,
    control,
    setValue,
    watch,
    trigger,
    errors = {},
    isSubmitting = false,
    layout = 'full'
}) => {
    // useFieldArray para gerenciar recursos dinamicamente
    const { fields, append, remove } = useFieldArray({
        control,
        name: 'resources'
    });

    // Hooks para dados dos comboboxes
    const { types, technologies, seniorities, loading, error: resourceError } = useResourceOptions();
    const toast = useToast();

    // Watch para recursos atuais
    const resources = watch('resources') || [];

    // Estado para seleção global de capacidade
    const [selection, setSelection] = useState<{
        typeId: string;
        technologyId: string;
        seniorityId: string;
    }>({
        typeId: '',
        technologyId: '',
        seniorityId: ''
    });
    const [selectionErrors, setSelectionErrors] = useState<Record<string, string>>({});
    const [defaultSeniorityId, setDefaultSeniorityId] = useState<string>('');

    // Debug: Log dos erros para development
    React.useEffect(() => {
        if (import.meta.env.DEV && errors.resources) {
            console.log('🐛 CapabilitiesFieldset - Erro de validação:', errors.resources);
            console.log('🐛 Tipo do erro:', typeof errors.resources);
            console.log('🐛 Estrutura completa:', JSON.stringify(errors.resources, null, 2));
        }
    }, [errors.resources]);

    // Estado local para validação de capacidade
    const [localValidationErrors, setLocalValidationErrors] = useState<Record<string, string>>({});

    const {
        searchTerm,
        setSearchTerm,
        isSearching,
        visibleTypes,
        visibleTechnologies,
    } = useCapabilitySearch({
        types,
        technologies,
        selectedTypeId: selection.typeId,
    });

    const resetSelection = () => {
        setSelection({
            typeId: '',
            technologyId: '',
            seniorityId: defaultSeniorityId || ''
        });
        setSelectionErrors({});
    };

    React.useEffect(() => {
        if (!seniorities.length) {
            return;
        }

        const preferredSeniority = seniorities.find(seniority => seniority.name.toLowerCase() === 'júnior') ?? seniorities[0];
        if (!preferredSeniority) {
            return;
        }

        setDefaultSeniorityId(preferredSeniority.id);
        setSelection(prev => {
            if (prev.seniorityId && seniorities.some(seniority => seniority.id === prev.seniorityId)) {
                return prev;
            }
            if (prev.seniorityId === preferredSeniority.id) {
                return prev;
            }
            return {
                ...prev,
                seniorityId: preferredSeniority.id
            };
        });
        setSelectionErrors(prev => {
            if (!prev.seniorityId) {
                return prev;
            }
            const next = { ...prev };
            delete next.seniorityId;
            return next;
        });
    }, [seniorities]);

    React.useEffect(() => {
        if (!visibleTypes.length) {
            setSelection(prev => {
                if (!prev.typeId && !prev.technologyId) {
                    return prev;
                }

                return {
                    ...prev,
                    typeId: '',
                    technologyId: ''
                };
            });
            return;
        }

        setSelection(prev => {
            if (prev.typeId && visibleTypes.some(type => type.id === prev.typeId)) {
                return prev;
            }

            const firstTypeId = visibleTypes[0].id;
            if (prev.typeId === firstTypeId && !prev.technologyId) {
                return prev;
            }

            return {
                ...prev,
                typeId: firstTypeId,
                technologyId: ''
            };
        });
    }, [visibleTypes, setSelection]);

    React.useEffect(() => {
        if (!selection.technologyId) {
            return;
        }

        if (visibleTechnologies.some((technology) => technology.id === selection.technologyId)) {
            return;
        }

        setSelection(prev => {
            if (!prev.technologyId) {
                return prev;
            }

            return {
                ...prev,
                technologyId: ''
            };
        });
    }, [visibleTechnologies, selection.technologyId, setSelection]);

    const handleSelectType = (type: ResourceType) => {
        setSelection(prev => ({
            ...prev,
            typeId: type.id,
            technologyId: ''
        }));
        setSelectionErrors(prev => {
            const next = { ...prev };
            delete next.typeId;
            delete next.technologyId;
            return next;
        });
    };

    const handleSelectTechnology = (technology: Technology) => {
        setSelection(prev => ({
            ...prev,
            typeId: technology.resourceTypeId || prev.typeId,
            technologyId: technology.id
        }));
        setSelectionErrors(prev => {
            const next = { ...prev };
            delete next.typeId;
            delete next.technologyId;
            return next;
        });
    };

    const handleSelectSeniority = (seniority: Seniority | null) => {
        setSelection(prev => ({
            ...prev,
            seniorityId: seniority?.id || ''
        }));
        setSelectionErrors(prev => {
            const next = { ...prev };
            delete next.seniorityId;
            return next;
        });
    };

    // Helper local para layout baseado no contexto
    const getLayoutByContext = (context: CollaboratorFormContext) => {
        const isCompact = context === 'PROFILE_UPDATE';
        return {
            fieldsetStyle: isCompact
                ? "border border-gray-200 p-3 rounded-md"
                : "border border-gray-200 p-4 rounded-md",
            legendStyle: isCompact
                ? "text-sm font-medium px-1 text-gray-600"
                : "text-sm font-medium px-1 text-gray-400"
        };
    };


    // Validação local manual para adicionar capacidade
    const validateNewCapability = () => {
        const errors: Record<string, string> = {};

        // Verificar se há recursos incompletos
        const incompleteResources = resources.filter((resource: Resource) => {
            return !resource.typeId || !resource.technologyId || !resource.seniorityId;
        });

        if (incompleteResources.length > 0) {
            errors.incomplete = `Complete ${incompleteResources.length === 1 ? 'a capacidade incompleta' : `as ${incompleteResources.length} capacidades incompletas`} antes de adicionar uma nova`;
        }

        setLocalValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmitSelection = () => {
        if (!validateNewCapability()) {
            return;
        }

        const errors: Record<string, string> = {};
        if (!selection.typeId) {
            errors.typeId = 'Selecione um tipo de capacidade.';
        }
        if (!selection.technologyId) {
            errors.technologyId = 'Selecione uma tecnologia relacionada.';
        }
        if (!selection.seniorityId) {
            errors.seniorityId = 'Defina a senioridade.';
        }
        setSelectionErrors(errors);
        if (Object.keys(errors).length > 0) {
            return;
        }

        const newResource: Resource = {
            typeId: selection.typeId,
            technologyId: selection.technologyId,
            seniorityId: selection.seniorityId,
            isPrimary: resources.length === 0
        };
        append(newResource);
        trigger?.('resources');
        setLocalValidationErrors({});
        setSelection(prev => ({
            ...prev,
            technologyId: '',
            seniorityId: prev.seniorityId
        }));
        setSelectionErrors({});
        toast.success('Capacidade adicionada à lista');
    };

    // Helper para remover recurso
    const handleRemoveResource = (index: number) => {
        const removedResource = resources[index];

        remove(index);

        // Se removeu o recurso principal e ainda há recursos, tornar o primeiro principal
        if (removedResource?.isPrimary && resources.length > 1) {
            const remainingResources = resources.filter((_: any, i: number) => i !== index);
            if (remainingResources.length > 0) {
                setValue(`resources.0.isPrimary`, true);
            }
        }

        trigger?.('resources');
        toast.info('Capacidade removida da lista');
    };

    // Helper para definir recurso principal
    const handleSetPrimaryResource = (index: number) => {
        // Limpar outros recursos como principais
        resources.forEach((_: any, i: number) => {
            setValue(`resources.${i}.isPrimary`, i === index);
        });
        trigger?.('resources');
    };

    // Layout contextual
    const layoutConfig = getLayoutByContext(context);

    // Verificar se o fieldset deve ser exibido usando schema unificado
    if (!collaboratorSchemaHelpers.shouldShowField('resources', context)) {
        return null;
    }

    return (
        <fieldset className={layoutConfig.fieldsetStyle}>
            <legend className={layoutConfig.legendStyle}>Capacidades</legend>
            <div className="mb-6 space-y-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <p className="text-sm font-semibold text-gray-800">
                            Adicionar nova capacidade
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                            Escolha um tipo, selecione a tecnologia correspondente e defina a senioridade antes de adicionar.
                        </p>
                    </div>
                    <CapabilitySearchInput
                        value={searchTerm}
                        onChange={setSearchTerm}
                        className="sm:w-72"
                    />
                </div>

                <div className="grid gap-4 lg:grid-cols-2">
                    <div>
                        <ResourceTypeList
                            types={visibleTypes}
                            selectedTypeId={selection.typeId}
                            onSelect={handleSelectType}
                            loading={loading}
                            error={resourceError}
                            disabled={isSubmitting}
                            emptyMessage={isSearching ? 'Nenhum tipo corresponde à sua busca.' : undefined}
                        />
                        {selectionErrors.typeId && (
                            <p className="mt-2 text-sm text-red-600">{selectionErrors.typeId}</p>
                        )}
                    </div>
                    <div>
                        <TechnologyList
                            technologies={visibleTechnologies}
                            types={types}
                            selectedTechnologyId={selection.technologyId}
                            onSelect={handleSelectTechnology}
                            loading={loading}
                            error={resourceError}
                            disabled={isSubmitting}
                            isSearching={isSearching}
                        />
                        {selectionErrors.technologyId && (
                            <p className="mt-2 text-sm text-red-600">{selectionErrors.technologyId}</p>
                        )}
                    </div>
                </div>

                <div>
                    <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                        Senioridade
                    </span>
                    {loading ? (
                        <div className="mt-2 text-sm text-gray-500">Carregando senioridades...</div>
                    ) : resourceError ? (
                        <div className="mt-2 text-sm text-red-600">{resourceError}</div>
                    ) : seniorities.length === 0 ? (
                        <div className="mt-2 text-sm text-gray-500">Nenhuma senioridade cadastrada.</div>
                    ) : (
                        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                            <div role="radiogroup" className="flex flex-wrap items-center gap-2">
                                {seniorities.map((seniority) => {
                                    const checked = selection.seniorityId === seniority.id;
                                    return (
                                        <label
                                            key={seniority.id}
                                            className={`flex items-center gap-2 px-3 py-2 text-sm transition-colors ${checked ? 'text-indigo-700 bg-indigo-50 rounded-md' : 'text-gray-700 hover:text-indigo-600'} ${isSubmitting ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                                        >
                                            <input
                                                type="radio"
                                                name="capability-seniority"
                                                value={seniority.id}
                                                checked={checked}
                                                onChange={() => handleSelectSeniority(seniority)}
                                                disabled={isSubmitting}
                                                className="h-4 w-4 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                                            />
                                            <span>{seniority.name}</span>
                                        </label>
                                    );
                                })}
                            </div>
                            <div className="flex items-center gap-2">
                                {(selection.typeId || selection.technologyId || selection.seniorityId) && (
                                    <button
                                        type="button"
                                        onClick={resetSelection}
                                        className="inline-flex items-center justify-center h-9 w-9 text-gray-500 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                                        disabled={isSubmitting}
                                        title="Limpar seleção"
                                    >
                                        <ArrowPathIcon className="w-5 h-5" />
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={handleSubmitSelection}
                                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                    disabled={isSubmitting || loading || types.length === 0 || seniorities.length === 0}
                                >
                                    <PlusCircleIcon className="w-5 h-5" />
                                    Adicionar capacidade
                                </button>
                            </div>
                        </div>
                    )}
                    {selectionErrors.seniorityId && (
                        <p className="mt-2 text-sm text-red-600">{selectionErrors.seniorityId}</p>
                    )}
                </div>

                {localValidationErrors.incomplete && (
                    <p className="text-sm text-red-600">{localValidationErrors.incomplete}</p>
                )}
            </div>

            <div className="grid gap-4 mb-4 pt-2 sm:grid-cols-2">
                {fields.map((field, index) => {
                    const resource = resources[index] || {};
                    const selectedType = types.find(type => type.id === resource.typeId);
                    const selectedTechnology = technologies.find(tech => tech.id === resource.technologyId);
                    const selectedSeniority = seniorities.find(seniority => seniority.id === resource.seniorityId);
                    return (
                        <div
                            key={field.id}
                            className="border border-gray-200 rounded-lg bg-white shadow-sm p-4"
                        >
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        onClick={() => handleSetPrimaryResource(index)}
                                        className="flex items-center justify-center h-9 w-9 rounded-full border border-transparent hover:border-yellow-300 transition-colors"
                                        title={resource.isPrimary ? 'Capacidade principal' : 'Marcar como capacidade principal'}
                                        disabled={isSubmitting}
                                    >
                                        {resource.isPrimary ? (
                                            <StarSolidIcon className="w-6 h-6 text-yellow-500" />
                                        ) : (
                                            <StarIcon className="w-6 h-6 text-gray-400 hover:text-yellow-500" />
                                        )}
                                    </button>
                                    <p className="text-sm font-medium text-gray-800">
                                        {(selectedTechnology?.name || 'Tecnologia não definida')} - {(selectedType?.name || 'Tipo não definido')}
                                    </p>
                                </div>
                                <div className="flex items-center gap-3">
                                    {selectedSeniority && (
                                        <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                                            {selectedSeniority.name}
                                        </span>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveResource(index)}
                                        className="inline-flex items-center justify-center h-9 w-9 text-red-600 hover:text-red-700 transition-colors"
                                        disabled={isSubmitting}
                                        title="Remover capacidade"
                                    >
                                        <TrashIcon className="w-5 h-5" />
                                    </button>
                                </div>
                            </div>

                            <Controller
                                name={`resources.${index}.typeId`}
                                control={control}
                                render={({ field: controllerField }) => (
                                    <input type="hidden" {...controllerField} />
                                )}
                            />
                            <Controller
                                name={`resources.${index}.technologyId`}
                                control={control}
                                render={({ field: controllerField }) => (
                                    <input type="hidden" {...controllerField} />
                                )}
                            />
                            <Controller
                                name={`resources.${index}.seniorityId`}
                                control={control}
                                render={({ field: controllerField }) => (
                                    <input type="hidden" {...controllerField} />
                                )}
                            />
                            <Controller
                                name={`resources.${index}.isPrimary`}
                                control={control}
                                render={({ field: controllerField }) => (
                                    <input type="hidden" {...controllerField} />
                                )}
                            />

                            {(errors.resources?.[index]?.typeId || errors.resources?.[index]?.technologyId || errors.resources?.[index]?.seniorityId) && (
                                <div className="mt-3 text-sm text-red-600 space-y-1">
                                    {errors.resources?.[index]?.typeId && <p>{errors.resources[index].typeId.message}</p>}
                                    {errors.resources?.[index]?.technologyId && <p>{errors.resources[index].technologyId.message}</p>}
                                    {errors.resources?.[index]?.seniorityId && <p>{errors.resources[index].seniorityId.message}</p>}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
            {/* Mensagens de erro do schema - multiple scenarios */}
            {errors.resources && (
                <div className="mt-2 space-y-1">
                    {/* Erro como string direta */}
                    {typeof errors.resources === 'string' && (
                        <p className="text-sm text-red-600">{errors.resources}</p>
                    )}

                    {/* Erro como objeto com message */}
                    {typeof errors.resources === 'object' && 'message' in errors.resources && (
                        <p className="text-sm text-red-600">{errors.resources.message}</p>
                    )}

                    {/* Erro como objeto com propriedade root */}
                    {typeof errors.resources === 'object' && 'root' in errors.resources && errors.resources.root && (
                        <p className="text-sm text-red-600">{errors.resources.root.message}</p>
                    )}

                    {/* Erro como array de objetos com mensagens */}
                    {Array.isArray(errors.resources) && errors.resources.some(err => err && typeof err === 'object' && 'message' in err) && (
                        <div className="space-y-1">
                            {errors.resources.map((err, index) =>
                                err && typeof err === 'object' && 'message' in err ? (
                                    <p key={index} className="text-sm text-red-600">{err.message}</p>
                                ) : null
                            )}
                        </div>
                    )}

                    {/* Debug: mostrar estrutura do erro em desenvolvimento */}
                    {import.meta.env.DEV && typeof errors.resources === 'object' && !('message' in errors.resources) && !('root' in errors.resources) && !Array.isArray(errors.resources) && (
                        <p className="text-xs text-orange-600">Erro de validação: {JSON.stringify(errors.resources)}</p>
                    )}
                </div>
            )}

            {/* Aviso sobre dependências */}
            {(types.length === 0 || seniorities.length === 0) && (
                <p className="text-xs text-yellow-700 mt-2">
                    Tipos de recurso ou senioridades não cadastrados para adicionar mais capacidades.
                </p>
            )}
        </fieldset>
    );
};

export default CapabilitiesFieldset; 
